import {
  designAreaProblems,
  normalizeDesignAnswer,
  resolveDesignAreaConfig,
  type DesignAreaAnswer,
  type DesignAreaConfig,
  type DesignProblem,
} from './design-area.js';
import { resizeDesignLayer } from './designer-parity.js';
import { STICKER_DEFAULTS } from './sticker.js';

/**
 * Print locations — Zazzle's "Front" / "Front Pocket" choice on one side of a
 * product.
 *
 * Each location is an ordinary design-area zone of its own, with its own size
 * and its own place on the print file and on the mockup. What makes them
 * ALTERNATIVES is `config.printLocation.group`: zones that share a group are
 * the places one design may go, and the shopper picks one. Nothing new is
 * printed and no zone changes shape — the storefront moves the design into the
 * chosen zone and leaves the others empty, and an empty zone prints nothing.
 * That keeps the print pipeline, the price and the validator exactly as they
 * are for any other zone.
 */
export interface PrintLocation {
  /** Zones sharing this key are alternatives on one side. */
  group: string;
  /** What the location picker says: "Na grudima", "Džep". */
  name: string;
}

export const PRINT_LOCATION_LIMITS = {
  group: '1–40 of a–z, 0–9, - or _',
  nameMax: 60,
} as const;

const GROUP = /^[a-z0-9_-]{1,40}$/;

export function parsePrintLocation(raw: unknown): PrintLocation {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) throw new Error('Invalid print location');
  const extra = Object.keys(raw).filter((key) => key !== 'group' && key !== 'name');
  if (extra.length) throw new Error(`Unknown print location field: ${extra[0]}`);
  const { group, name } = raw as Record<string, unknown>;
  if (typeof group !== 'string' || !GROUP.test(group)) {
    throw new Error(`Print location group must be ${PRINT_LOCATION_LIMITS.group}`);
  }
  const label = typeof name === 'string' ? name.trim() : '';
  if (!label || label.length > PRINT_LOCATION_LIMITS.nameMax) {
    throw new Error(`Print location name must be 1–${PRINT_LOCATION_LIMITS.nameMax} characters`);
  }
  return { group, name: label };
}

/** Studio's and MCP's one operation: set (or with `null`, clear) a zone's location. */
export function configurePrintLocation(config: DesignAreaConfig, location: unknown): DesignAreaConfig {
  if (location === null) {
    const { printLocation: _gone, ...rest } = config;
    return rest;
  }
  return { ...config, printLocation: parsePrintLocation(location) };
}

/**
 * Which zone of a group is the chosen one: the first that holds a design, else
 * the shopper's last pick (a location chosen before anything was designed),
 * else the first in order. The answers decide whenever there is a design, so
 * the storefront, a restored cart and a share link all agree without storing
 * a separate pick.
 */
export function activePrintLocation(
  zoneIds: readonly string[],
  isEmpty: (zoneId: string) => boolean,
  preferred?: string,
): string | undefined {
  return zoneIds.find((id) => !isEmpty(id))
    ?? (preferred !== undefined && zoneIds.includes(preferred) ? preferred : zoneIds[0]);
}

/**
 * The design moved from one location to another: every layer scaled by the
 * one factor that fits the old zone into the new one, about the centre, so
 * the composition keeps its proportions. The target zone's own rules decide
 * what survives — a Sticker it does not offer is dropped, one it requires is
 * added — and what the shopper still has to fix is returned rather than
 * silently changed.
 */
export function movePrintLocationDesign(
  from: DesignAreaConfig,
  to: DesignAreaConfig,
  answer: DesignAreaAnswer,
): { answer: DesignAreaAnswer; problems: DesignProblem[] } {
  const source = resolveDesignAreaConfig(from);
  const target = resolveDesignAreaConfig(to);
  const factor = Math.min(target.sizeMm.w / source.sizeMm.w, target.sizeMm.h / source.sizeMm.h);
  if (!(factor > 0) || !Number.isFinite(factor)) throw new Error('Invalid print location size');
  const moved = structuredClone(answer);
  for (const layer of moved.layers) {
    layer.box.x = target.sizeMm.w / 2 + (layer.box.x - source.sizeMm.w / 2) * factor;
    layer.box.y = target.sizeMm.h / 2 + (layer.box.y - source.sizeMm.h / 2) * factor;
    resizeDesignLayer(layer, factor);
    // A pocket is small; letters are never made smaller than the product
    // allows. The box grows with them so the text is not squeezed.
    if (layer.kind === 'text' && layer.sizeMm < target.text.minSizeMm) {
      const grow = target.text.minSizeMm / layer.sizeMm;
      resizeDesignLayer(layer, grow);
    }
  }
  if (to.sticker?.allowed !== true) delete moved.sticker;
  else if (to.sticker.required && !moved.sticker) moved.sticker = to.sticker.defaults ?? STICKER_DEFAULTS;
  if (!target.allow.background) delete moved.background;
  const normalized = normalizeDesignAnswer(moved) ?? moved;
  const problems = designAreaProblems(to, normalized)
    .filter((problem) => !['low-resolution', 'asset-unknown', 'library-not-allowed'].includes(problem.code));
  return { answer: normalized, problems };
}

export const PRINT_LOCATION_CAPABILITIES = {
  operation: 'configure_print_location',
  field: 'config.printLocation: { group, name } | null',
  group: PRINT_LOCATION_LIMITS.group,
  nameMax: PRINT_LOCATION_LIMITS.nameMax,
  model: 'Zones on one side sharing a group are alternative print locations (e.g. chest vs pocket). Each keeps its own size and print/mockup placement. The shopper designs one; the storefront moves the design proportionally when switching and leaves the others empty. An empty zone prints nothing and is not charged.',
  active: 'The first zone of the group holding a design, else the first in option order.',
} as const;
